import { cloneDeep, isNil } from "lodash";
import { BID_AUTO_VALUE } from "src/types/sponsored-ads";
import { getKnownBrowser } from "src/utils/browser";
import type { FormatterOptions } from "./common";
import { FORMATTERS_TRANSLATION_NAMESPACE } from "./config";

export const MONEY_TK = "MONEY_TK";

export const PRICE_FACTOR = 100;

export interface FormatPriceOptions extends FormatterOptions {
    noDecimal?: boolean;
    showHyphenIfZero?: boolean;
    showZeroIfNil?: boolean;
    priceFactor?: number;
    withMillion?: boolean;
    millionSymbol?: string;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
}

export function formatPrice(
    amount: number,
    {
        currencyCode,
        noDecimal = false,
        showHyphenIfZero = false,
        showZeroIfNil = false,
        priceFactor = PRICE_FACTOR,
        language,
        withMillion = false,
        millionSymbol,
        minimumFractionDigits: minimumFractionDigitsProps,
        maximumFractionDigits: maximumFractionDigitsProps,
    }: FormatPriceOptions
): string {
    // clone juste au cas ou si la reference est utilisé ailleurs dans un des nombreux endroit ou cette fonction est call
    let clonedAmount = cloneDeep(amount);

    if (showZeroIfNil && isNil(clonedAmount)) {
        clonedAmount = 0;
    }

    if (
        clonedAmount === null ||
        clonedAmount === undefined ||
        (showHyphenIfZero && clonedAmount === 0)
    ) {
        return "—";
    }

    let price = clonedAmount / priceFactor;
    if (noDecimal) {
        // Rounding negative numbers close to 0 can lead to -0
        // to avoid this we add 0 to the result which converts -0 to 0
        price = Math.round(price) + 0;
    }

    const browser = getKnownBrowser();
    const currencyDisplay =
        browser.browser === "safari" && browser.version < 14 ? "symbol" : "narrowSymbol";

    if ((withMillion && price >= 1000000) || price <= -1000000) {
        const priceInMillions = price / 1000000;

        const formatter = new Intl.NumberFormat(language, {
            style: "currency",
            currency: currencyCode,
            currencyDisplay,
            minimumFractionDigits: 0,
            maximumFractionDigits: priceInMillions % 1 === 0 ? 0 : 1,
        });

        let formattedPrice = formatter.format(priceInMillions);

        if (language === "fr-FR") {
            formattedPrice = formattedPrice
                .replace(/(\d+([.,]\d+)?)(\s?\D+)/, `$1 ${millionSymbol}$3`)
                .replace(/\s(?=\S*$)/, "");
        } else {
            formattedPrice = formattedPrice
                .replace(/(\D+)(\s?\d+([.,]\d+)?)/, `$1$2 ${millionSymbol}`)
                .replace(/\s(?=\S*$)/, "");
        }
        return formattedPrice;
    }

    let minimumFractionDigits: boolean | number = noDecimal && 0;
    if (minimumFractionDigitsProps !== undefined) {
        minimumFractionDigits = minimumFractionDigitsProps;
    }

    const { format }: Intl.NumberFormat = new Intl.NumberFormat(language, {
        style: "currency",
        currency: currencyCode,
        currencyDisplay,
        minimumFractionDigits,
        maximumFractionDigits: maximumFractionDigitsProps,
    });

    return format(price);
}

export function addCurrencySymbol(
    amount: string,
    { language, currencySymbol }: FormatterOptions
): string {
    if (language === "fr-FR") {
        return `${amount} ${currencySymbol}`;
    } else {
        return `${currencySymbol}${amount}`;
    }
}

export function formatBid(amount: number, options: FormatPriceOptions): string {
    const { translateRaw } = options;

    if (amount === BID_AUTO_VALUE) {
        return translateRaw(`${FORMATTERS_TRANSLATION_NAMESPACE}:${MONEY_TK}.auto-bid`);
    }

    return formatPrice(amount, options);
}
